import { BOARD_SIZE, CANVAS_WIDTH } from "../../game/constants";
import { bpmForLevel } from "../../game/audioConfig";
import { isMobileViewport } from "../../game/platform";
import { inferSwipeIntentTarget, type Rect } from "../../game/input";
import type {
  BoardState,
  CollapseAnimState,
  RotationAnimState,
  SwapAnimState,
  TileData,
} from "../../game/types";
import type { RenderModel } from "../renderModel";
import type { VersusFeedbackMode } from "../../game/versusFeedbackPolicy";
import {
  GAME_FONT,
  mFont,
  posKey,
  roundedRectPath,
  isSinglePlayerHudPolishPhase,
  musicPulseForLevel,
  grooveMeterScaleForFill,
  grooveMeterTitle,
  grooveFilledSegmentCount,
  grooveShimmerStrengthForFill,
  grooveShimmerSpeedForFill,
  resolveGrooveReadyCueStyle,
  resolveGrooveActiveOutline,
  resolveDisplayedSpinsCount,
  DrawBoardResult,
  DrawBoardOptions,
} from "../renderConstants";

// ── Types ────────────────────────────────────────────────────────────────

/** Mutable board layout reference — tps is written during drawBoard. */
export interface BoardLayoutRef {
  tps: number;
  bps: number;
  box: number;
  boardY: number;
}

export interface BoardDeps {
  ctx: CanvasRenderingContext2D;
  boardLayout: BoardLayoutRef;
  reducedMotionPreferred: boolean;
  nowMs: () => number;
  nowSec: () => number;

  /** Layout metrics forwarded from the renderer. */
  layout: {
    spinsIndicatorY: number;
    controlRowY: number;
    characterBaseY: number;
  };

  /** Pre-loaded images. */
  turntableLeftActiveImage: HTMLImageElement | null;
  turntableRightActiveImage: HTMLImageElement | null;
  turntableLeftImage: HTMLImageElement | null;
  turntableRightImage: HTMLImageElement | null;
  mixerImage: HTMLImageElement | null;

  /** Sub-rendering callbacks forwarded from the renderer class. */
  maybeEmitRowRushAmbientParticles: (targetRows: number[], feedbackMode: VersusFeedbackMode, goalDone: boolean) => void;
  drawRotationBoard: (anim: RotationAnimState, targetRows: number[], goalDone: boolean) => void;
  drawSwapBoard: (board: BoardState, swapAnim: SwapAnimState, enhancedRejectFx: boolean, targetRows: number[], goalDone: boolean) => void;
  drawCollapseBoard: (board: BoardState, collapseAnim: CollapseAnimState, feedbackMode: VersusFeedbackMode, targetRows: number[], goalDone: boolean) => void;
  drawBoardSurface: (board: BoardState, opts?: {
    hiddenTileDestinationKey?: string;
    hintCellKeys?: Set<string>;
    hintActive?: boolean;
    targetRows?: number[];
    rowRushGoalDone?: boolean;
  }) => void;
  drawTileSprite: (tile: TileData, x: number, y: number, size: number) => void;
  drawCharacterPortrait: (path: string, x: number, y: number, w: number, h: number) => void;
  drawPortraitGrooveActiveFx: (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => void;
}

// ── Extracted screen function ────────────────────────────────────────────

export function drawBoardScreen(
  deps: BoardDeps,
  model: RenderModel,
  feedbackMode: VersusFeedbackMode = "full",
  options: DrawBoardOptions = {}
): DrawBoardResult {
  const ctx = deps.ctx;
  const board = model.run?.board;
  const run = model.run;
  const singlePlayerHudPolish = isSinglePlayerHudPolishPhase(model.phase);
  const hideCornerCharacter = options.hideCornerCharacter ?? model.hideCornerCharacter ?? false;
  const bl = deps.boardLayout;
  bl.tps = bl.bps / Math.max(1, board?.size ?? BOARD_SIZE);

  // ============================================================
  //  Phase 6: New layout order — Groove → Spins → Controls → Shop
  // ============================================================
  const vinylSize = 58;
  const mixerW = 58;
  const mixerH = vinylSize;
  const ctrlGap = 4;
  const tripleW = vinylSize + ctrlGap + mixerW + ctrlGap + vinylSize;
  const tripleLeftX = Math.floor((CANVAS_WIDTH - tripleW) / 2);

  // In mobile versus, attach groove meter flush with board bottom (hiding grid edge)
  const mobileVersus = isMobileViewport() && model.versusMatch != null;
  const grooveMeterBarW = tripleW;
  const grooveMeterY = mobileVersus
    ? (bl.boardY + bl.bps + 6)  // 6px below board bottom
    : deps.layout.spinsIndicatorY;
  const grooveMeterRect = {
    x: Math.floor((CANVAS_WIDTH - grooveMeterBarW) / 2),
    y: grooveMeterY,
    w: grooveMeterBarW,
    h: 30
  };

  // Controls row: hug groove meter in mobile versus, normal layout otherwise
  const ctrlY = mobileVersus
    ? (grooveMeterY + 30 + 4)  // 4px gap below groove meter
    : deps.layout.controlRowY;
  const rotateLeft: Rect = { x: tripleLeftX, y: ctrlY, w: vinylSize, h: vinylSize };
  const mixerRect: Rect = { x: tripleLeftX + vinylSize + ctrlGap, y: ctrlY, w: mixerW, h: mixerH };
  const rotateRight: Rect = { x: mixerRect.x + mixerW + ctrlGap, y: ctrlY, w: vinylSize, h: vinylSize };
  // Spins pill removed — counts shown on CDJs; keep rect for key trigger area
  const keyTriggerRect: Rect = { ...grooveMeterRect };

  // ============================================================
  //  Board rendering (same as before)
  // ============================================================
  if (board != null) {
    const drag = model.dragState;
    const isDragging = drag != null && board != null;
    const rowRushTargetRows = model.soloMode === "row-rush" ? model.goalTargetRows : [];
    const rowRushGoalDone = model.soloMode === "row-rush"
      && (
        model.phase === "RUN_LEVELUP"
        || (model.goalRemaining <= 0 && (model.goalTarget ?? 0) > 0)
      );
    const dragDistance = isDragging
      ? Math.hypot(
          drag.canvasX - (bl.box + drag.startCell.col * bl.tps + bl.tps / 2),
          drag.canvasY - (bl.boardY + drag.startCell.row * bl.tps + bl.tps / 2)
        )
      : 0;
    const isFullDrag = isDragging && dragDistance >= bl.tps / 2;

    const anyAnimActive = model.rotationAnim?.active || model.swapAnim?.active || model.collapseAnim?.active;
    deps.maybeEmitRowRushAmbientParticles(rowRushTargetRows, feedbackMode, rowRushGoalDone);

    if (model.rotationAnim?.active) {
      deps.drawRotationBoard(model.rotationAnim, rowRushTargetRows, rowRushGoalDone);
    } else if (model.swapAnim?.active) {
      deps.drawSwapBoard(board, model.swapAnim, singlePlayerHudPolish, rowRushTargetRows, rowRushGoalDone);
    } else if (model.collapseAnim?.active) {
      deps.drawCollapseBoard(board, model.collapseAnim, feedbackMode, rowRushTargetRows, rowRushGoalDone);
    } else {
      const hintCellKeys = model.hintActive ? new Set(model.hintCells.map((cell) => posKey(cell.row, cell.col))) : undefined;
      const hiddenForDragKey = isFullDrag ? posKey(drag.startCell.row, drag.startCell.col) : undefined;
      deps.drawBoardSurface(board, {
        hiddenTileDestinationKey: hiddenForDragKey,
        hintCellKeys,
        hintActive: model.hintActive,
        targetRows: rowRushTargetRows,
        rowRushGoalDone
      });
    }

    if (isDragging && !isFullDrag && !anyAnimActive) {
      const pulse = 0.5 + 0.5 * Math.sin(deps.nowMs() / 150);
      const startCenterX = bl.box + drag.startCell.col * bl.tps + bl.tps / 2;
      const startCenterY = bl.boardY + drag.startCell.row * bl.tps + bl.tps / 2;
      const dragDxPx = drag.canvasX - startCenterX;
      const dragDyPx = drag.canvasY - startCenterY;
      const intentTarget = singlePlayerHudPolish
        ? inferSwipeIntentTarget(
            drag.startCell,
            dragDxPx,
            dragDyPx,
            board.cells.length || BOARD_SIZE,
            bl.tps * 0.2
          )
        : null;
      if (singlePlayerHudPolish) {
        const highlightCells = intentTarget == null
          ? [drag.startCell]
          : [drag.startCell, intentTarget];
        for (let i = 0; i < highlightCells.length; i += 1) {
          const cell = highlightCells[i];
          const hx = bl.box + cell.col * bl.tps;
          const hy = bl.boardY + cell.row * bl.tps;
          const leading = i === 0;
          const outerAlpha = deps.reducedMotionPreferred
            ? (leading ? 0.8 : 0.72)
            : (leading ? 0.54 : 0.44) + pulse * (leading ? 0.4 : 0.34);
          const innerAlpha = deps.reducedMotionPreferred
            ? (leading ? 0.36 : 0.28)
            : (leading ? 0.2 : 0.16) + pulse * (leading ? 0.24 : 0.18);
          ctx.save();
          ctx.fillStyle = `rgba(84,214,255,${innerAlpha.toFixed(3)})`;
          ctx.fillRect(hx + 3.2, hy + 3.2, bl.tps - 6.4, bl.tps - 6.4);
          ctx.strokeStyle = `rgba(112,242,255,${outerAlpha.toFixed(3)})`;
          ctx.lineWidth = 1.5;
          ctx.shadowColor = `rgba(86,226,255,${(outerAlpha * 0.9).toFixed(3)})`;
          ctx.shadowBlur = deps.reducedMotionPreferred ? 3 : 8;
          ctx.strokeRect(hx + 1.2, hy + 1.2, bl.tps - 2.4, bl.tps - 2.4);
          ctx.shadowBlur = 0;
          ctx.strokeStyle = `rgba(228,255,255,${(0.38 + innerAlpha).toFixed(3)})`;
          ctx.lineWidth = 0.9;
          ctx.strokeRect(hx + 2.6, hy + 2.6, bl.tps - 5.2, bl.tps - 5.2);
          ctx.restore();
        }
        ctx.lineWidth = 1;
      } else {
        const sx = bl.box + drag.startCell.col * bl.tps;
        const sy = bl.boardY + drag.startCell.row * bl.tps;
        ctx.strokeStyle = `rgba(255,220,80,${(0.5 + pulse * 0.5).toFixed(3)})`;
        ctx.lineWidth = 2;
        ctx.strokeRect(sx + 1, sy + 1, bl.tps - 2, bl.tps - 2);
        ctx.lineWidth = 1;
      }
    }

    if (isFullDrag && !anyAnimActive) {
      const dragTile = board.cells[drag.startCell.row]?.[drag.startCell.col]?.tile;
      if (dragTile != null) {
        const drawSize = Math.round((bl.tps - 2) * 1.15);
        const drawX = Math.max(bl.box, Math.min(bl.box + bl.bps - drawSize, drag.canvasX - drawSize / 2));
        const drawY = Math.max(bl.boardY, Math.min(bl.boardY + bl.bps - drawSize, drag.canvasY - drawSize / 2));
        ctx.save();
        ctx.shadowBlur = 6;
        ctx.shadowColor = "rgba(0,0,0,0.4)";
        deps.drawTileSprite(dragTile, drawX, drawY, drawSize);
        ctx.restore();
      }
    }

    if (model.armedPowerup === "HINT" && model.hintFirstPick != null) {
      const pulse = 0.5 + 0.5 * Math.sin(deps.nowMs() / 120);
      const sx = bl.box + model.hintFirstPick.col * bl.tps;
      const sy = bl.boardY + model.hintFirstPick.row * bl.tps;
      ctx.strokeStyle = `rgba(${Math.round(170 + pulse * 60)},${Math.round(120 + pulse * 80)},255,1)`;
      ctx.lineWidth = 2;
      ctx.strokeRect(sx + 1, sy + 1, bl.tps - 2, bl.tps - 2);
      ctx.lineWidth = 1;
    }

    const reshuffleStep = model.collapseAnim?.active
      ? model.collapseAnim.steps[model.collapseAnim.stepIndex]
      : null;
    if (reshuffleStep?.specialFx === "RESHUFFLE" && reshuffleStep.overlayText != null) {
      const boardCenterX = bl.box + bl.bps / 2;
      const boardCenterY = bl.boardY + bl.bps / 2;
      ctx.save();
      ctx.fillStyle = "rgba(8,14,28,0.28)";
      ctx.fillRect(bl.box, bl.boardY, bl.bps, bl.bps);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.shadowColor = "rgba(0,0,0,0.45)";
      ctx.shadowBlur = 8;
      ctx.fillStyle = "#f8fbff";
      ctx.font = mFont("bold", 12);
      ctx.fillText(reshuffleStep.overlayText.headline, boardCenterX, boardCenterY - 12);
      ctx.shadowBlur = 4;
      ctx.fillStyle = "rgba(214,236,255,0.96)";
      ctx.font = mFont("bold", 8);
      ctx.fillText(reshuffleStep.overlayText.subline, boardCenterX, boardCenterY + 12);
      ctx.restore();
    }

    if (model.paused) {
      ctx.fillStyle = "rgba(8,14,28,0.62)";
      ctx.fillRect(bl.box, bl.boardY, bl.bps, bl.bps);
      ctx.fillStyle = "#c8e5ff";
      ctx.font = `bold 11px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("PAUSED", bl.box + bl.bps / 2, bl.boardY + bl.bps / 2);
    }
  }

  const grooveCueNowMs = deps.nowMs();

  // ============================================================
  //  Phase 8: Groove meter (audio-reactive + fill-reactive emphasis)
  //  Skipped in mobile versus mode (groove bars live next to skins instead)
  // ============================================================
  {
  const groove = run?.groove;
  const grooveFillFrac = Math.max(0, Math.min(1, groove?.groove ?? 0));
  const grooveActive = groove?.active === true;
  const grooveArmed = groove?.armed === true;
  const grooveReady = grooveArmed && !grooveActive && (groove?.readyWindowSec ?? 0) > 0;
  const grooveReadyCue = grooveReady
    ? resolveGrooveReadyCueStyle(grooveCueNowMs, deps.reducedMotionPreferred)
    : null;
  const levelForMusic = Math.max(1, Math.min(20, run?.level ?? 1));
  const songTimeSec = run?.beat.songTimeSec ?? deps.nowSec();
  const grooveScale = grooveMeterScaleForFill(grooveFillFrac);

  ctx.save();
  const grooveCx = grooveMeterRect.x + grooveMeterRect.w / 2;
  const grooveCy = grooveMeterRect.y + grooveMeterRect.h / 2;
  ctx.translate(grooveCx, grooveCy);
  ctx.scale(grooveScale, grooveScale);
  ctx.translate(-grooveCx, -grooveCy);
  if (grooveReadyCue != null) {
    ctx.translate(grooveCx, grooveCy);
    ctx.scale(grooveReadyCue.scale, grooveReadyCue.scale);
    ctx.translate(-grooveCx, -grooveCy);
  }

  roundedRectPath(ctx, grooveMeterRect.x, grooveMeterRect.y, grooveMeterRect.w, grooveMeterRect.h, 6);
  ctx.fillStyle = "rgba(14,24,42,0.92)";
  ctx.fill();
  roundedRectPath(ctx, grooveMeterRect.x, grooveMeterRect.y, grooveMeterRect.w, grooveMeterRect.h, 6);
  if (grooveActive) {
    const grooveOutline = resolveGrooveActiveOutline(
      songTimeSec,
      run?.beat.bpm ?? bpmForLevel(levelForMusic),
      grooveFillFrac
    );
    const activeGrad = ctx.createLinearGradient(
      grooveMeterRect.x,
      grooveMeterRect.y,
      grooveMeterRect.x + grooveMeterRect.w,
      grooveMeterRect.y + grooveMeterRect.h
    );
    activeGrad.addColorStop(0, `hsla(${grooveOutline.hueA.toFixed(1)},100%,62%,${grooveOutline.alpha.toFixed(3)})`);
    activeGrad.addColorStop(0.5, `hsla(${grooveOutline.hueB.toFixed(1)},100%,60%,${grooveOutline.alpha.toFixed(3)})`);
    activeGrad.addColorStop(1, `hsla(${grooveOutline.hueC.toFixed(1)},100%,62%,${(grooveOutline.alpha * 0.95).toFixed(3)})`);
    ctx.save();
    ctx.shadowColor = `hsla(${grooveOutline.hueA.toFixed(1)},100%,58%,${grooveOutline.shadowAlpha.toFixed(3)})`;
    ctx.shadowBlur = grooveOutline.shadowBlur;
    ctx.strokeStyle = activeGrad;
    ctx.lineWidth = grooveOutline.lineWidth;
    ctx.stroke();
    ctx.restore();
  } else if (grooveReadyCue != null) {
    ctx.save();
    ctx.shadowColor = grooveReadyCue.shadowColor;
    ctx.shadowBlur = grooveReadyCue.shadowBlur;
    ctx.strokeStyle = grooveReadyCue.outlineColor;
    ctx.lineWidth = grooveReadyCue.lineWidth;
    ctx.stroke();
    ctx.restore();
  } else {
    ctx.strokeStyle = "#4a6a9f";
    ctx.lineWidth = 1.2 + grooveFillFrac * 1.2;
    ctx.stroke();
  }
  ctx.lineWidth = 1;

  const barsGap = 1;
  const barsCount = 20;
  const barsStartX = grooveMeterRect.x;
  const barsTotalW = grooveMeterRect.w;
  const baseBarW = Math.floor((barsTotalW - (barsCount - 1) * barsGap) / barsCount);
  const extraPx = barsTotalW - (barsCount * baseBarW + (barsCount - 1) * barsGap);
  const barsTopY = grooveMeterRect.y + 1;
  const barsMaxH = grooveMeterRect.h - 2;
  // Pre-compute each bar's x and width so they span edge-to-edge
  const barPositions: { x: number; w: number }[] = [];
  {
    let cx = barsStartX;
    for (let i = 0; i < barsCount; i++) {
      const w = baseBarW + (i < extraPx ? 1 : 0);
      barPositions.push({ x: cx, w });
      cx += w + barsGap;
    }
  }

  {
    const filledSegments = grooveFilledSegmentCount(grooveFillFrac, barsCount);
    const shimmerStrength = grooveShimmerStrengthForFill(grooveFillFrac, deps.reducedMotionPreferred);
    const shimmerSpeed = grooveShimmerSpeedForFill(grooveFillFrac, deps.reducedMotionPreferred);

    ctx.save();
    roundedRectPath(ctx, grooveMeterRect.x, grooveMeterRect.y, grooveMeterRect.w, grooveMeterRect.h, 6);
    ctx.clip();

    for (let i = 0; i < barsCount; i += 1) {
      const bp = barPositions[i];
      ctx.fillStyle = i < filledSegments
        ? "rgba(46,66,98,0.72)"
        : "rgba(20,30,48,0.78)";
      ctx.fillRect(bp.x, barsTopY, bp.w, barsMaxH);
    }

    for (let i = 1; i < barsCount; i += 1) {
      const sx = barPositions[i].x - barsGap * 0.5;
      ctx.strokeStyle = "rgba(8,14,24,0.42)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sx, barsTopY + 1);
      ctx.lineTo(sx, barsTopY + barsMaxH - 1);
      ctx.stroke();
    }

    if (filledSegments > 0) {
      const lastFilled = barPositions[filledSegments - 1];
      const fillW = lastFilled.x + lastFilled.w - barsStartX;
      const fillGrad = ctx.createLinearGradient(0, barsTopY, 0, barsTopY + barsMaxH);
      if (grooveActive) {
        fillGrad.addColorStop(0, "rgba(255,170,248,0.98)");
        fillGrad.addColorStop(1, "rgba(118,72,240,0.92)");
      } else if (grooveReady) {
        fillGrad.addColorStop(0, "rgba(164,255,248,0.98)");
        fillGrad.addColorStop(1, "rgba(78,216,255,0.92)");
      } else {
        fillGrad.addColorStop(0, "rgba(164,186,255,0.96)");
        fillGrad.addColorStop(1, "rgba(86,120,236,0.9)");
      }
      ctx.save();
      ctx.beginPath();
      ctx.rect(barsStartX, barsTopY, fillW, barsMaxH);
      ctx.clip();
      for (let i = 0; i < filledSegments; i += 1) {
        const bp = barPositions[i];
        ctx.fillStyle = fillGrad;
        ctx.fillRect(bp.x, barsTopY, bp.w, barsMaxH);
      }

      const shimmerTravel = barsTotalW + fillW + 36;
      const shimmerX = barsStartX - barsTotalW + ((songTimeSec * shimmerSpeed) % shimmerTravel);
      ctx.save();
      ctx.translate(shimmerX, barsTopY);
      ctx.rotate(-Math.PI / 6);
      const shimmerGrad = ctx.createLinearGradient(0, 0, 34, 0);
      shimmerGrad.addColorStop(0, "rgba(255,255,255,0)");
      shimmerGrad.addColorStop(0.48, `rgba(220,248,255,${shimmerStrength.toFixed(3)})`);
      shimmerGrad.addColorStop(0.52, `rgba(255,255,255,${Math.min(0.65, shimmerStrength + 0.2).toFixed(3)})`);
      shimmerGrad.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = shimmerGrad;
      ctx.fillRect(-26, -barsMaxH, 88, barsMaxH * 3);
      ctx.restore();
      ctx.restore();

      const edgeX = barsStartX + fillW;
      ctx.strokeStyle = grooveReady || grooveActive
        ? "rgba(236,255,255,0.95)"
        : "rgba(196,232,255,0.9)";
      ctx.lineWidth = grooveFillFrac >= 0.95 ? 1.8 : 1.4;
      ctx.beginPath();
      ctx.moveTo(edgeX + 0.5, barsTopY + 1);
      ctx.lineTo(edgeX + 0.5, barsTopY + barsMaxH - 1);
      ctx.stroke();
    }

    ctx.restore();
    ctx.lineWidth = 1;
  }

  const titleFill = grooveActive ? "#fff0ff" : grooveReady ? "#e0ffff" : "#ddeeff";
  const titleX = grooveMeterRect.x + grooveMeterRect.w / 2;
  const titleY = grooveMeterRect.y + grooveMeterRect.h / 2;
  const titleText = grooveActive
    ? "GROOVE!"
    : grooveMeterTitle(grooveReady);
  ctx.font = `bold 6px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if (singlePlayerHudPolish) {
    ctx.lineWidth = isMobileViewport() ? 1.2 : 1.4;
    ctx.strokeStyle = "rgba(8,14,28,0.95)";
    ctx.strokeText(titleText, titleX, titleY);
    ctx.save();
    ctx.shadowColor = "rgba(4,8,18,0.6)";
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    ctx.shadowBlur = 0;
    ctx.fillStyle = titleFill;
    ctx.fillText(titleText, titleX, titleY);
    ctx.restore();
    ctx.lineWidth = 1;
  } else {
    ctx.fillStyle = titleFill;
    ctx.fillText(titleText, titleX, titleY);
  }

  ctx.restore();
  } // end groove meter skip (mobile versus)

  // Spins pill removed — spin counts are shown on CDJ turntables

  // ============================================================
  //  Phase 7: Controls — Vinyl turntables + Mixer
  // ============================================================
  const manualRotationAvailable =
    model.soloMode === "row-rush"
      ? true
      : (run?.rotationKeys ?? 0) > 0
        || (run?.groove.active === true && (run?.groove.freeRotationsRemaining ?? 0) > 0);
  const isLeft = model.soloMode === "row-rush"
    ? model.pendingRotation === "LEFT"
    : model.pendingRotation !== "RIGHT";
  const isRight = model.soloMode === "row-rush"
    ? model.pendingRotation === "RIGHT"
    : model.pendingRotation === "RIGHT";

  // Timer progress (1 = full time, 0 = rotation imminent)
  let timerProgress = 1;
  if (run != null) {
    const full = run.beat.rotationEveryBeats * run.beat.beatDurationSec;
    timerProgress = Math.max(0, Math.min(1, run.beat.timeToNextRotationSec / full));
  }

  // --- Helper: draw vinyl turntable button ---
  const drawVinyl = (rect: Rect, dir: "left" | "right", selected: boolean, locked: boolean): void => {
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;

    const ttActiveImg = dir === "left" ? deps.turntableLeftActiveImage : deps.turntableRightActiveImage;
    const ttDefaultImg = dir === "left" ? deps.turntableLeftImage : deps.turntableRightImage;
    const ttImg = (selected && ttActiveImg != null) ? ttActiveImg : ttDefaultImg;
    if (ttImg != null) {
      // Sprite path
      ctx.save();
      if (locked) ctx.globalAlpha = 0.4;
      ctx.drawImage(ttImg, rect.x, rect.y, rect.w, rect.h);
      ctx.restore();
    } else {
      // Fallback: procedural vinyl
      roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 8);
      ctx.fillStyle = locked ? "#1a1e28" : "#141c30";
      ctx.fill();

      // Outer ring
      ctx.beginPath();
      ctx.arc(cx, cy, rect.w * 0.42, 0, Math.PI * 2);
      ctx.strokeStyle = locked ? "#2a2e3a" : "#3a4a68";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // Middle ring
      ctx.beginPath();
      ctx.arc(cx, cy, rect.w * 0.3, 0, Math.PI * 2);
      ctx.strokeStyle = locked ? "#222636" : "#2a3a58";
      ctx.lineWidth = 1;
      ctx.stroke();
      // Center dot
      ctx.beginPath();
      ctx.arc(cx, cy, 4, 0, Math.PI * 2);
      ctx.fillStyle = locked ? "#3a3e4a" : "#ff7adf";
      ctx.fill();

      roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 8);
      ctx.strokeStyle = locked ? "#3a3e4a" : dir === "left" ? "#5580b0" : "#8060a0";
      ctx.lineWidth = 1.2;
      ctx.stroke();
    }

    // Timer arc around vinyl (circular arc showing timerProgress on selected direction)
    if (selected && !locked && model.soloMode !== "row-rush") {
      const arcRadius = rect.w * 0.46;
      const startAngle = -Math.PI / 2;
      const endAngle = startAngle + (1 - timerProgress) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(cx, cy, arcRadius, startAngle, endAngle);
      const arcColor = timerProgress >= 0.5 ? "#00d4ff"
        : timerProgress >= 0.25 ? "#ffd700"
        : "#ff5050";
      ctx.strokeStyle = arcColor;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.lineWidth = 1;
    }

  };

  drawVinyl(rotateLeft, "left", isLeft, !manualRotationAvailable);
  drawVinyl(rotateRight, "right", isRight, !manualRotationAvailable);

  // --- Spins count overlaid on each CDJ ---
  {
    const spinsStr = model.soloMode === "row-rush"
      ? (model.pendingRotation ?? "RND")
      : `x${resolveDisplayedSpinsCount(run?.rotationKeys ?? 0, run?.groove)}`;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = mFont("bold", model.soloMode === "row-rush" ? 6 : 8);
    ctx.lineWidth = 3;
    ctx.strokeStyle = "rgba(0,0,0,0.85)";
    ctx.fillStyle = "#e8f7ff";
    const labelDrop = 3;
    const leftCX = rotateLeft.x + rotateLeft.w / 2;
    const leftCY = rotateLeft.y + rotateLeft.h / 2 + labelDrop;
    ctx.strokeText(spinsStr, leftCX, leftCY);
    ctx.fillText(spinsStr, leftCX, leftCY);
    const rightCX = rotateRight.x + rotateRight.w / 2;
    const rightCY = rotateRight.y + rotateRight.h / 2 + labelDrop;
    ctx.strokeText(spinsStr, rightCX, rightCY);
    ctx.fillText(spinsStr, rightCX, rightCY);
    ctx.restore();
  }

  // --- Center mixer (GROOVE MODE / KEY trigger) ---
  const grooveArmedCenter = run?.groove?.armed === true;
  const grooveActiveCenter = run?.groove?.active === true;
  const grooveReadyCenter = grooveArmedCenter && !grooveActiveCenter && (run?.groove?.readyWindowSec ?? 0) > 0;
  const grooveReadyCueCenter = grooveReadyCenter
    ? resolveGrooveReadyCueStyle(grooveCueNowMs, deps.reducedMotionPreferred)
    : null;
  ctx.save();
  if (grooveReadyCueCenter != null) {
    const mixerCx = mixerRect.x + mixerRect.w / 2;
    const mixerCy = mixerRect.y + mixerRect.h / 2;
    ctx.translate(mixerCx, mixerCy);
    ctx.scale(grooveReadyCueCenter.scale, grooveReadyCueCenter.scale);
    ctx.translate(-mixerCx, -mixerCy);
  }
  if (deps.mixerImage != null) {
    ctx.drawImage(deps.mixerImage, mixerRect.x, mixerRect.y, mixerRect.w, mixerRect.h);
  } else {
    // Fallback: dark rounded rect with fader lines
    roundedRectPath(ctx, mixerRect.x, mixerRect.y, mixerRect.w, mixerRect.h, 8);
    ctx.fillStyle = "#141c30";
    ctx.fill();
    roundedRectPath(ctx, mixerRect.x, mixerRect.y, mixerRect.w, mixerRect.h, 8);
    ctx.strokeStyle = "#4a6a9f";
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // Fader lines (3 vertical lines with small rect knobs)
    for (let f = 0; f < 3; f++) {
      const fx = mixerRect.x + 14 + f * 14;
      const fy1 = mixerRect.y + 10;
      const fy2 = mixerRect.y + mixerRect.h - 14;
      ctx.strokeStyle = "#3a4a68";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(fx, fy1);
      ctx.lineTo(fx, fy2);
      ctx.stroke();
      // Knob
      const knobY = fy1 + (fy2 - fy1) * (0.3 + f * 0.2);
      ctx.fillStyle = "#6a8ac0";
      ctx.fillRect(fx - 3, knobY - 2, 6, 4);
    }
  }

  // Groove state overlay on mixer
  if (grooveReadyCueCenter != null || grooveActiveCenter) {
    ctx.save();
    if (grooveActiveCenter) {
      const gPulse = 0.5 + 0.5 * Math.sin(grooveCueNowMs / 100);
      // Rainbow cycling border
      const hue = (grooveCueNowMs / 3) % 360;
      ctx.shadowColor = `hsla(${hue.toFixed(1)},100%,65%,${(0.6 + gPulse * 0.4).toFixed(3)})`;
      ctx.shadowBlur = 8 + gPulse * 10;
      roundedRectPath(ctx, mixerRect.x, mixerRect.y, mixerRect.w, mixerRect.h, 8);
      ctx.strokeStyle = `hsla(${hue.toFixed(1)},100%,70%,1)`;
      ctx.lineWidth = 2.5;
      ctx.stroke();
    } else if (grooveReadyCueCenter != null) {
      ctx.shadowColor = grooveReadyCueCenter.shadowColor;
      ctx.shadowBlur = grooveReadyCueCenter.shadowBlur;
      roundedRectPath(ctx, mixerRect.x, mixerRect.y, mixerRect.w, mixerRect.h, 8);
      ctx.strokeStyle = grooveReadyCueCenter.outlineColor;
      ctx.lineWidth = grooveReadyCueCenter.lineWidth;
      ctx.stroke();
    }
    ctx.restore();
  }
  ctx.restore();

  // ============================================================
  //  Phase 9: Bottom HUD (character left, powerups + money right)
  // ============================================================

  // Character portrait on the bottom-left, bobbing to music beat.
  let characterHubRect: Rect | undefined;
  if (!hideCornerCharacter) {
    const gameChar = model.equippedCharacter;
    const gpSize = 160;
    const gpX = -50;
    const gpTimeSec = deps.nowSec();
    const runLevel = model.run?.level ?? 1;
    const gpPulse = musicPulseForLevel(gpTimeSec, runLevel);
    const gpBob = (gpPulse - 0.5) * 3;
    const gpBaseY = deps.layout.characterBaseY;
    const gpY = gpBaseY + gpBob;
    deps.drawCharacterPortrait(gameChar.imageSmallPath, gpX, gpY, gpSize, gpSize);
    // Groove active FX on player portrait
    if (model.run?.groove.active === true) {
      deps.drawPortraitGrooveActiveFx(deps.ctx, gpX, gpY, gpSize);
    }
    characterHubRect = { x: 0, y: gpBaseY + 10, w: 70, h: 100 };
  }

  return {
    board: { x: bl.box, y: bl.boardY, w: bl.bps, h: bl.bps },
    boardGridSize: board?.size ?? BOARD_SIZE,
    rotateLeft,
    rotateRight,
    keyTrigger: keyTriggerRect,
    grooveMeter: grooveMeterRect,
    grooveMixer: mixerRect,
    characterHub: characterHubRect
  };
}
